import { refusalStatus, isStockBusy } from './refusal';

/**
 * Why a write was not sent, said to the person, and the predicates every queue asks
 * (MAP 225b). The refusals below happen on THIS device, before or instead of any network
 * attempt; none of them is the server saying no.
 */

/** Refused before any network attempt: nobody is signed in to make it for. */
export class UnownedWriteError extends Error {
  constructor() {
    super(
      'Nobody is signed in on this device, so this can’t be saved. Sign in and try again.',
    );
    this.name = 'UnownedWriteError';
  }
}

/** Refused before any network attempt: a replayable write needs an idempotency key. */
export class UnkeyedWriteError extends Error {
  constructor(path: string) {
    super(`Servwell couldn’t save this safely (${path} has no idempotency key).`);
    this.name = 'UnkeyedWriteError';
  }
}

/** Refused before any network attempt: the same write is already waiting here. */
export class DuplicateWriteError extends Error {
  constructor() {
    super('This is already saved on this device and waiting to send.');
    this.name = 'DuplicateWriteError';
  }
}

/**
 * The durable put was refused (a full disk, private mode), so the write is NOT sent: one
 * that is only safe once a request has succeeded is not safe while the request is in flight
 * (the photo queue's own rule, `upload-queue.ts`).
 */
export class StorageFullError extends Error {
  constructor(cause: unknown) {
    super(
      'This device is out of storage, so this change can’t be saved. Free up space and try again.',
    );
    this.name = 'StorageFullError';
    this.cause = cause;
  }
}

/**
 * Refused before any network attempt: an office write made while the account this tab draws
 * has not been confirmed by the network (an offline start). It could not be queued for
 * later (only a tech's writes queue, under the account that made them), and it may not be
 * sent before Servwell knows who is signed in here.
 */
export class NeedsConnectionError extends Error {
  constructor() {
    super('This needs a connection. Try again once you’re back online.');
    this.name = 'NeedsConnectionError';
  }
}

const PARKED = 'ParkedWriteError';

/**
 * The write guard would not send this write from this tab now. NOT a refusal by the server:
 * a replayable write keeps its row for the account that made it, and nothing goes to the
 * failed-write log. What the person is told depends on what still holds the write.
 */
export class ParkedWriteError extends Error {
  constructor(told: 'kept' | 'account-changed' | 'not-on-device') {
    super(
      told === 'kept'
        ? 'Saved on this device. It sends once Servwell has confirmed who is signed in here.'
        : told === 'account-changed'
          ? 'Not sent: who is signed in here has changed. Reload, then try again.'
          : 'Not sent: Servwell couldn’t find this change saved on this device. Try it again.',
    );
    this.name = PARKED;
  }
}

/**
 * The device is offline and the write may not go yet: the attempt fails the way a dead
 * connection does (status 0, retryable) without touching the network, so TanStack pauses it
 * in place, as it pauses any write made offline. Only for a replayable write waiting for the
 * session to be confirmed after an offline start.
 */
export class NoSignalError extends Error {
  constructor() {
    super('No signal. It’s saved on this device and sends when you’re back.');
    this.name = 'NoSignalError';
  }
}

function looksParked(e: unknown): boolean {
  return typeof e === 'object' && e !== null && (e as { name?: unknown }).name === PARKED;
}

/**
 * THE one predicate for "the write guard parked this": retry, the hand-off and every
 * failed/offline count ask it, and nothing else reads the class. tRPC wraps a link's error
 * (`TRPCClientError.from(new ParkedWriteError(…))` keeps it as `.cause`), so both are read.
 * By name, not `instanceof`, so a second copy of this module cannot hide it.
 */
export function isParked(err: unknown): boolean {
  return looksParked(err) || looksParked((err as { cause?: unknown } | null)?.cause);
}

/**
 * Kept for the account that made it, not refused: parked by the guard, or a 401 (signed
 * out, which signing in fixes). Neither is retried, neither goes to the failed-write log,
 * and neither counts as a failed write anywhere on screen.
 */
export function isKeptForAccount(err: unknown): boolean {
  return isParked(err) || refusalStatus(err) === 401;
}

/** Every refusal made on THIS device, by name (so a second copy of the module still counts). */
const REFUSED_HERE = new Set([
  'UnownedWriteError',
  'UnkeyedWriteError',
  'DuplicateWriteError',
  'StorageFullError',
  'NeedsConnectionError',
  PARKED,
]);

/**
 * What to tell the person about a write this device would not send, or null when the error
 * is something else (the server's answer, a dead connection). Read from the error, or from
 * its cause where tRPC wrapped it.
 */
export function refusedHereMessage(err: unknown): string | null {
  for (const e of [err, (err as { cause?: unknown } | null)?.cause]) {
    if (typeof e !== 'object' || e === null) continue;
    const { name, message } = e as { name?: unknown; message?: unknown };
    if (
      typeof name === 'string' &&
      REFUSED_HERE.has(name) &&
      typeof message === 'string'
    ) {
      return message;
    }
  }
  return null;
}

/** Three backoffs ordinarily; kept writes and a known busy delivery need deliberate recovery. */
export function shouldRetryWrite(failureCount: number, error: unknown): boolean {
  return !isKeptForAccount(error) && !isStockBusy(error) && failureCount < 3;
}
